import { __rest } from "tslib";
import React, { useMemo, useEffect, useState, createRef, useContext } from 'react';
import { imageIdToAkamaiSrc } from "@pb/utils/image/mappings/imageIdToAkamaiSrc/imageIdToAkamaiSrc";
import { getImageUrlBasedOnContainerWidth } from "@pb/utils/image/getImageUrl";
import { default as useBreakpoint } from "@pb/utils/reactHooks/useBreakpoint";
import { toHighDPIImageUrl } from "@pb/utils/url/toHighDPIImageUrl/toHighDPIImageUrl";
import { currentBrand } from "@pb/utils/brandConfig";
import { getLpPrefix } from "@pb/utils/getLpPrefix";
import { getLpDamUrl } from "@pb/utils/getLpPrefix";
import cn from 'classnames';
import IntersectionVisible from "../../../../helpers/IntersectionVisible";
import RectangleSkeleton from "../../../../helpers/Loaders/RectangleSkeleton";
import styles from './Image.module.scss';
import ImageDefaultPropsContext from './ImageDefaultPropsContext';
const getResolverPlaceholder = (imageSrc, resolverSettings, isDamImage) => {
    const resolverPlaceholderArray = imageSrc === null || imageSrc === void 0 ? void 0 : imageSrc.split('?');
    if (resolverPlaceholderArray === null || resolverPlaceholderArray === void 0 ? void 0 : resolverPlaceholderArray.length) {
        const { width, height } = resolverSettings;
        const resolverPlaceholderParams = `blank=width[${width}],height[${height}],color[none]&sink=format[gif]`;
        const brand = currentBrand;
        if (isDamImage) {
            resolverPlaceholderArray[0] = getLpPrefix(brand);
        }
        resolverPlaceholderArray[1] = resolverPlaceholderParams;
        return resolverPlaceholderArray === null || resolverPlaceholderArray === void 0 ? void 0 : resolverPlaceholderArray.join('?');
    }
    return '';
};
const resolverSettingsFromRatio = (reserveHeightAspectRatio) => {
    if (reserveHeightAspectRatio !== '' && reserveHeightAspectRatio !== null) {
        return {
            width: parseInt(reserveHeightAspectRatio.split('/')[0], 10),
            height: parseInt(reserveHeightAspectRatio.split('/')[1], 10),
        };
    }
    return {};
};
const getResolverDataAttributes = (imagePath, resolverSettings, isDamImage, shouldSetResolverQuality) => {
    var _a, _b;
    const dataAttribute = isDamImage ? 'data-resolvesrc' : 'data-resolvechain';
    const brand = currentBrand;
    const dataValue = isDamImage
        ? `${getLpDamUrl(brand) + decodeURIComponent(imagePath)}`
        : `${(_b = (_a = decodeURIComponent(imagePath)) === null || _a === void 0 ? void 0 : _a.match(/(set=source|type|ImageVersion|origin).*?(\])/g)) === null || _b === void 0 ? void 0 : _b.toString()}&call=url[file:/product/dynamic.chain]`;
    return {
        [dataAttribute]: dataValue,
        'data-resolveheight': resolverSettings.height,
        'data-resolvewidth': resolverSettings.width,
        'data-resolvequality': shouldSetResolverQuality === null || shouldSetResolverQuality === void 0 ? void 0 : shouldSetResolverQuality.toString(),
    };
};
const Image = (props) => {
    const defaultProps = useContext(ImageDefaultPropsContext);
    const allProps = Object.assign(Object.assign({}, defaultProps), props);
    const { altText, url, className, Tag = 'div', shouldLazyLoad = true, intersectionVisibleOptions, imageType, isBackgroundImage = false, reserveHeightAspectRatio = '', height, width, shouldShowSkeleton = true, resolverSettings, shouldUseResolver = false, shouldUseAkamai = false, shouldSetResolverQuality = true, shouldUseHighDPIImage, shouldUseDirectSrc, srcSet, sizes, imageId, isImageHeightAutoAdjustmentEnabled = false, shouldDelayLazyLoad = false, // prevent jittery scrolling by delaying lazy image loading
    lazyLoadDelayMs = 500, // default is current carousel scroll time default
    onLoad, handleOnLoad, isDamImage } = allProps, propsRest = __rest(allProps, ["altText", "url", "className", "Tag", "shouldLazyLoad", "intersectionVisibleOptions", "imageType", "isBackgroundImage", "reserveHeightAspectRatio", "height", "width", "shouldShowSkeleton", "resolverSettings", "shouldUseResolver", "shouldUseAkamai", "shouldSetResolverQuality", "shouldUseHighDPIImage", "shouldUseDirectSrc", "srcSet", "sizes", "imageId", "isImageHeightAutoAdjustmentEnabled", "shouldDelayLazyLoad", "lazyLoadDelayMs", "onLoad", "handleOnLoad", "isDamImage"]);
    const initialImageSrc = imageType ? '' : url;
    const [isIntersected, setIsIntersected] = useState(false);
    const srcSetProps = shouldLazyLoad && !isIntersected
        ? { srcSet: '', src: '' }
        : {
            srcSet,
            src: url,
        };
    // States
    const [isLoading, setIsLoading] = useState(true);
    const [imageSrc, setImageSrc] = useState(initialImageSrc);
    const [lazyImageUrl, setLazyImageUrl] = useState('');
    const [delayedSrc, setDelayedSrc] = useState(undefined);
    // If we are lazyLoading, use the lazyImageUrl state, else use the imageSrc immediately.
    const handledSrc = useMemo(() => {
        // If we are lazyLoading, set empty string '' as src, when image is scrolled into view (intersected) set imageSrc as src. Else use imageSrc immediately.
        const handledUrl = shouldLazyLoad && !isIntersected ? '' : imageSrc;
        return shouldUseHighDPIImage ? toHighDPIImageUrl(handledUrl) : handledUrl;
    }, [shouldUseHighDPIImage, shouldLazyLoad, isIntersected, imageSrc]);
    useEffect(() => {
        if (shouldDelayLazyLoad && isIntersected) {
            setTimeout(() => {
                setDelayedSrc(handledSrc);
            }, lazyLoadDelayMs);
        }
    }, [isIntersected]);
    const imgRef = createRef();
    const backgroundImageRef = createRef();
    const refElement = isBackgroundImage ? backgroundImageRef : imgRef;
    const useDevPrefixedUrl = process.env.USE_DEV_PREFIXED_LIQUID_PIXELS_URLS === 'enabled';
    const isXXL = useBreakpoint() === 'xxl';
    const isLifeArticle = () => {
        if (className && isXXL) {
            if (className.split(' ').filter((c) => c === 'life-article-image').length) {
                return true;
            }
        }
        return false;
    };
    // Get adjusted resolution image url for image src based on container width and image type. If image type is undefined, default image-sizes will be used.
    const getImageSrc = (src, containerWidth, type) => {
        if (shouldUseDirectSrc)
            return src;
        return getImageUrlBasedOnContainerWidth({
            src,
            containerWidth,
            type,
            useDevPrefixedUrl,
        });
    };
    const setAdjustedResolution = (src, parent) => {
        const parentWidth = parent.offsetWidth;
        if (parentWidth === 0) {
            setTimeout(() => {
                setAdjustedResolution(src, parent);
            }, 100);
        }
        else {
            const adjustedImageSrc = getImageSrc(src, parentWidth, imageType);
            setImageSrc(adjustedImageSrc);
        }
    };
    const handleImageLoaded = () => {
        // If image src value is not empty we set loading to false onLoad. onLoad runs even if img src attribute is empty.
        if (shouldDelayLazyLoad && delayedSrc) {
            setIsLoading(false);
        }
        else if (handledSrc) {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        var _a;
        if ((_a = imgRef.current) === null || _a === void 0 ? void 0 : _a.complete) {
            handleImageLoaded();
        }
    }, []);
    const handleOnShowLazyImg = (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                setIsIntersected(true);
            }
        });
    };
    // Effects
    useEffect(() => {
        const baseCondition = refElement && refElement.current && (url || imageId);
        if (!baseCondition) {
            return;
        }
        if (shouldUseAkamai) {
            const passedWidth = width || refElement.current.offsetWidth;
            const imageUrl = imageIdToAkamaiSrc(imageId, isDamImage, passedWidth, height);
            setImageSrc(imageUrl);
        }
        else if (imageType && !shouldUseResolver && !shouldUseAkamai) {
            // @ts-ignore: Object is possibly 'null'.
            setAdjustedResolution(url, refElement.current.parentNode);
        }
        else if (!imageType) {
            const imageUrl = url !== undefined ? url : imageIdToAkamaiSrc(imageId, isDamImage, width, height); // Akamai is always a dropback
            setImageSrc(imageUrl);
            if (shouldLazyLoad && lazyImageUrl) {
                setLazyImageUrl(imageUrl);
            }
        }
    }, [url, refElement.current, imageType, shouldLazyLoad]);
    useEffect(() => {
        if (!isLoading) {
            setIsLoading(true);
        }
    }, [imageSrc]);
    if (!(url || imageId))
        return null;
    const defaultIntersectionVisibleOptions = {
        rootMargin: '200% 0px',
    };
    const getReserveHeightStyle = () => {
        if (isImageHeightAutoAdjustmentEnabled) {
            return { height: `auto` };
        }
        if (isLifeArticle()) {
            return { paddingBottom: `calc(1140px / (${reserveHeightAspectRatio}))` };
        }
        return { paddingBottom: `calc(100% / (${reserveHeightAspectRatio}))` };
    };
    const reserveHeightStyle = getReserveHeightStyle();
    const fetchResolverSettings = resolverSettings || resolverSettingsFromRatio(reserveHeightAspectRatio);
    if (isBackgroundImage) {
        if (shouldLazyLoad) {
            return (React.createElement(IntersectionVisible, Object.assign({ onIntersect: handleOnShowLazyImg, active: !isIntersected, options: Object.assign(Object.assign({}, defaultIntersectionVisibleOptions), intersectionVisibleOptions), className: cn(styles.backgroundImage, reserveHeightAspectRatio && styles.reserveHeight, className), Tag: Tag, style: Object.assign({ backgroundImage: `url('${handledSrc}')` }, reserveHeightStyle), role: "img", "aria-label": altText }, propsRest),
                React.createElement("noscript", null,
                    React.createElement("img", { ref: imgRef, src: url, alt: altText })),
                propsRest.children));
        }
        return (React.createElement(Tag, Object.assign({ className: cn(styles.backgroundImage, reserveHeightAspectRatio && styles.reserveHeight, className), style: Object.assign({ backgroundImage: `url(${handledSrc})` }, reserveHeightStyle) }, propsRest, { role: "img", "aria-label": altText }),
            React.createElement("noscript", null,
                React.createElement("img", { ref: imgRef, src: url, alt: altText })),
            propsRest.children));
    }
    const renderImg = (React.createElement("img", Object.assign({ ref: imgRef, className: cn(styles.image, shouldUseResolver && 'Resolve', props.imageClassName), onLoad: () => {
            handleImageLoaded();
            if (onLoad)
                onLoad();
            handleOnLoad === null || handleOnLoad === void 0 ? void 0 : handleOnLoad();
        }, onError: handleImageLoaded, alt: altText, sizes: sizes, loading: props === null || props === void 0 ? void 0 : props.loading }, (srcSet && srcSetProps), { src: shouldUseResolver
            ? getResolverPlaceholder(url, fetchResolverSettings, isDamImage)
            : shouldDelayLazyLoad
                ? delayedSrc
                : handledSrc }, (height && !reserveHeightAspectRatio && { height }), (width && !reserveHeightAspectRatio && { width }), (shouldUseResolver &&
        getResolverDataAttributes(url, fetchResolverSettings, isDamImage, shouldSetResolverQuality)))));
    const renderSkeleton = isLoading && shouldShowSkeleton && (React.createElement(RectangleSkeleton, { className: styles.skeleton, height: "auto", shouldUseShimmerAnimation: false }));
    if (shouldLazyLoad && !isBackgroundImage) {
        // Default value for rootMargin: 200% 0, means that the observed image is intersected (loaded) when it reaches 200% root(viewport)-height distance offset.
        return (React.createElement(IntersectionVisible, Object.assign({ onIntersect: handleOnShowLazyImg, active: !isIntersected, options: Object.assign(Object.assign({}, defaultIntersectionVisibleOptions), intersectionVisibleOptions), className: cn(styles.imageWrapper, reserveHeightAspectRatio && styles.reserveHeight, className), Tag: Tag }, propsRest, { style: Object.assign({}, reserveHeightStyle) }),
            renderImg,
            renderSkeleton,
            props.children));
    }
    return (React.createElement(Tag, Object.assign({ className: cn(styles.imageWrapper, reserveHeightAspectRatio && styles.reserveHeight, className) }, propsRest, { style: Object.assign(Object.assign({}, reserveHeightStyle), propsRest.style) }),
        renderImg,
        renderSkeleton,
        props.children));
};
export default Image;
//# sourceMappingURL=Image.js.map